import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  Mail,
  RefreshCw,
  Search,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import type { FormEvent } from "react";

import {
  getPlatformInvitations,
  type PlatformInvitation,
} from "@/api/platformInvitations";
import { InvitationDetailsPanel } from "@/components/platform/invitations/InvitationDetailsPanel";
import { EmptyState } from "@/components/ui/EmptyState";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { PageContainer } from "@/design-system";

const INVITATION_STATUSES = [
  "ALL",
  "PENDING",
  "ACCEPTED",
  "EXPIRED",
  "REVOKED",
];

function formatDate(value?: string | null) {
  if (!value) {
    return "�";
  }

  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getAdministratorName(
  invitation: PlatformInvitation,
) {
  const name = [
    invitation.invited_first_name,
    invitation.invited_last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || "Administrator";
}

function getStatusClasses(status: string) {
  switch (status.toUpperCase()) {
    case "ACCEPTED":
      return "border-green-200 bg-green-100 text-green-700";

    case "PENDING":
      return "border-amber-200 bg-amber-100 text-amber-700";

    case "EXPIRED":
      return "border-slate-200 bg-slate-100 text-slate-700";

    case "REVOKED":
      return "border-red-200 bg-red-100 text-red-700";

    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
}

export function PlatformInvitationsPage() {
  const [searchInput, setSearchInput] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [status, setStatus] =
    useState("ALL");

  const [
    selectedInvitationId,
    setSelectedInvitationId,
  ] = useState<string | null>(null);

  const queryParams = useMemo(
    () => ({
      search: search || undefined,
      status:
        status === "ALL"
          ? undefined
          : status,
      offset: 0,
      limit: 50,
    }),
    [search, status],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      "platform-invitations",
      queryParams,
    ],
    queryFn: () =>
      getPlatformInvitations(queryParams),
  });

  const selectedInvitation =
    data?.items.find(
      (invitation) =>
        invitation.id === selectedInvitationId,
    ) ?? null;

  useEffect(() => {
    if (!data?.items.length) {
      setSelectedInvitationId(null);
      return;
    }

    const selectedStillExists =
      data.items.some(
        (invitation) =>
          invitation.id === selectedInvitationId,
      );

    if (!selectedStillExists) {
      setSelectedInvitationId(
        data.items[0].id,
      );
    }
  }, [data, selectedInvitationId]);

  function handleSearchSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();
    setSearch(searchInput.trim());
  }

  function handleClearFilters() {
    setSearchInput("");
    setSearch("");
    setStatus("ALL");
  }

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-slate-100 p-2">
              <Mail className="h-5 w-5 text-slate-700" />
            </div>

            <div>
              <h1 className="text-2xl font-semibold text-slate-950">
                Invitations
              </h1>

              <p className="mt-1 text-sm text-slate-600">
                Manage church administrator invitations
                and onboarding access.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              className={
                isFetching
                  ? "h-4 w-4 animate-spin"
                  : "h-4 w-4"
              }
            />

            Refresh
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row">
            <form
              onSubmit={handleSearchSubmit}
              className="flex flex-1 gap-2"
            >
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) =>
                    setSearchInput(event.target.value)
                  }
                  placeholder="Search church, administrator, or email"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                />
              </div>

              <button
                type="submit"
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Search
              </button>
            </form>

            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value)
              }
              className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              {INVITATION_STATUSES.map(
                (option) => (
                  <option
                    key={option}
                    value={option}
                  >
                    {option === "ALL"
                      ? "All statuses"
                      : option}
                  </option>
                ),
              )}
            </select>

            {(search || status !== "ALL") && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h2 className="font-semibold text-red-900">
              Unable to load invitations
            </h2>

            <p className="mt-1 text-sm text-red-700">
              Refresh the page or verify that the
              platform invitations endpoint is available.
            </p>
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white">
            <EmptyState
              title={
                search || status !== "ALL"
                  ? "No matching invitations"
                  : "No invitations"
              }
              description={
                search || status !== "ALL"
                  ? "Try changing your search or status filter."
                  : "No invitations have been created."
              }
            />
          </div>
        ) : (
          <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[minmax(320px,38%)_minmax(0,62%)]">
            <section className="border-b border-slate-200 lg:border-b-0 lg:border-r">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <h2 className="font-semibold text-slate-950">
                    Invitation records
                  </h2>

                  <p className="mt-1 text-sm text-slate-500">
                    {data.total} invitation
                    {data.total === 1 ? "" : "s"}
                  </p>
                </div>

                {isFetching && (
                  <span className="text-sm text-slate-500">
                    Updating�
                  </span>
                )}
              </div>

              <div className="max-h-[760px] overflow-y-auto">
                {data.items.map((invitation) => {
                  const isSelected =
                    selectedInvitationId ===
                    invitation.id;

                  return (
                    <button
                      key={invitation.id}
                      type="button"
                      onClick={() =>
                        setSelectedInvitationId(
                          invitation.id,
                        )
                      }
                      className={`flex w-full items-start gap-3 border-b border-slate-100 px-5 py-4 text-left transition last:border-b-0 ${
                        isSelected
                          ? "bg-slate-100"
                          : "bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <p className="truncate font-semibold text-slate-950">
                            {invitation.church_name}
                          </p>

                          <span
                            className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getStatusClasses(
                              invitation.status,
                            )}`}
                          >
                            {invitation.status}
                          </span>
                        </div>

                        <p className="mt-2 truncate text-sm font-medium text-slate-700">
                          {getAdministratorName(
                            invitation,
                          )}
                        </p>

                        <p className="mt-1 truncate text-sm text-slate-500">
                          {invitation.invited_email}
                        </p>

                        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
                          <span>
                            Expires{" "}
                            {formatDate(
                              invitation.expires_at,
                            )}
                          </span>

                          <span>
                            {invitation.send_count} send
                            {invitation.send_count === 1
                              ? ""
                              : "s"}
                          </span>
                        </div>
                      </div>

                      <ChevronRight
                        className={`mt-1 h-4 w-4 shrink-0 ${
                          isSelected
                            ? "text-slate-900"
                            : "text-slate-400"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="min-w-0">
              <InvitationDetailsPanel
                invitation={selectedInvitation}
                onInvitationReplaced={(
                    replacementInvitation,
                ) => {
                    setSelectedInvitationId(
                    replacementInvitation.id,
                    );
                }}
              />
            </section>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
